import * as React from 'react'

import { styled } from '~/styled'
import { cn } from '~/styled'
import { ToggleGroup } from '~/utilities/radix-overrides/toggle-group'

type RootType = {
  orientation?: 'horizontal' | 'vertical'
  isFullWidth?: boolean
  gap?: 0 | 1 | 2 | 3
  wrap?: boolean
}

export const StyledRoot = styled(ToggleGroup.Root, {
  base: ['w-fit'],
  variants: {
    isFullWidth: {
      true: [
        '**:data-radix-collection-item:basis-0',
        '**:data-radix-collection-item:grow',
        'w-full'
      ],
      false: ['w-auto']
    },
    hasGap: {
      true: ['**:data-radix-collection-item:rounded-md'],
      false: [
        '**:data-radix-collection-item:not-last:before:absolute',
        "**:data-radix-collection-item:not-last:before:content-['']",
        '**:data-radix-collection-item:relative',
        'rounded-md'
      ]
    },
    direction: {
      column: [],
      row: []
    },
    theme: {
      standard: [],
      modern: ['bg-grey-200']
    }
  },
  compoundVariants: [
    {
      hasGap: false,
      direction: 'row',
      theme: 'standard',
      class: [
        '**:data-radix-collection-item:first:rounded-l-md',
        '**:data-radix-collection-item:last:rounded-r-md',
        '**:data-radix-collection-item:not-first:border-l-transparent',
        '**:data-radix-collection-item:not-last:before:h-[calc(100%+2px)]',
        '**:data-radix-collection-item:not-last:before:right-0',
        '**:data-radix-collection-item:not-last:before:-top-px',
        '**:data-radix-collection-item:not-last:before:translate-x-3/2',
        '**:data-radix-collection-item:not-last:before:w-px',
        '**:data-radix-collection-item:not-last:border-r-transparent',
        '**:data-radix-collection-item:rounded-none'
      ]
    },
    {
      hasGap: false,
      direction: 'column',
      theme: 'standard',
      class: [
        '**:data-radix-collection-item:first:rounded-t-sm',
        '**:data-radix-collection-item:last:rounded-b-sm',
        '**:data-radix-collection-item:not-first:border-t-transparent',
        '**:data-radix-collection-item:not-last:before:bottom-0',
        '**:data-radix-collection-item:not-last:before:h-px',
        '**:data-radix-collection-item:not-last:before:-left-px',
        '**:data-radix-collection-item:not-last:before:translate-y-3/2',
        '**:data-radix-collection-item:not-last:before:w-[calc(100%+2px)]',
        '**:data-radix-collection-item:not-last:border-b-transparent'
      ]
    },
    {
      hasGap: false,
      theme: 'modern',
      class: []
    },
    {
      hasGap: false,
      theme: 'standard',
      class: [
        '**:data-radix-collection-item:bg-transparent',
        '**:data-radix-collection-item:rounded-none',
        'bg-white'
      ]
    }
  ]
})

const gapClassMap = {
  0: 'gap-0.5',
  1: 'gap-1',
  2: 'gap-2',
  3: 'gap-3'
}

const orientationToDirection = (orientation) =>
  orientation === 'horizontal' ? 'row' : 'column'

export const ToggleGroupRoot = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof StyledRoot> & RootType
>(
  (
    {
      orientation = 'horizontal',
      gap,
      isFullWidth,
      children,
      theme = 'standard',
      wrap,
      ...rest
    },
    ref
  ) => {
    const hasGap = typeof gap === 'number'
    const direction = orientationToDirection(orientation)

    return (
      <StyledRoot
        ref={ref}
        direction={direction}
        hasGap={hasGap}
        isFullWidth={isFullWidth}
        orientation={orientation}
        theme={theme}
        {...rest}
      >
        <div
          className={cn(
            'flex',
            hasGap && gapClassMap[gap],
            orientation === 'horizontal' ? 'flex-row' : 'flex-col',
            wrap ? 'flex-wrap' : 'flex-nowrap'
          )}
        >
          {
            React.Children.toArray(children).map((child) => {
              if (React.isValidElement<{ theme?: typeof theme }>(child)) {
                return React.cloneElement(child, { ...child.props, theme })
              }
              return child
            }) as React.ReactElement<unknown>[]
          }
        </div>
      </StyledRoot>
    )
  }
)
